import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ReactElement } from "react";

import { FrequencyProgress } from "@/common/components/FrequencyProgress";
import StarsStateIcon from "@/common/components/StarsStateIcon";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { useFeatureTargetingContextAttributeValues } from "@/feature/data/useFeatureTargetingContextAttributesData";
import { FeedbackSatisfactionIcon } from "@/feedback/components/FeedbackSatisfaction";
import { ErrorResponse } from "@reflag/shared/api";
import { COMPANY_ID_FIELD_KEY } from "@reflag/shared/attributeFilter";

import { commonQueryKeys } from "./commonQueryKeys";

type Result = {
  label: string;
  value: string;
  icon?: ReactElement;
};

const cacheTime = 30 * 1000; // 30 seconds

export type EntityType =
  | "event"
  | "company"
  | "feature-targeting-context-company"
  | "feature-targeting-context-user"
  | "feature-targeting-context-other"
  | "feature-frequency"
  | "feature-satisfaction"
  | "feature-funnelStep"
  | "segment"
  | "feature-flag";

export function useAttributeValues({
  entityType,
  entityValue,
  attributeKey,
  searchValue,
}: {
  entityType: EntityType;
  entityValue?: string;
  attributeKey?: string;
  searchValue?: string;
}):
  | { data?: Result[]; isFetching: boolean }
  | UseQueryResult<Result[], AxiosResponse<ErrorResponse>> {
  const { appId, envId } = useEnvironment();

  // The "Company ID" system attribute is stored under the "id" key in
  // attribute_suggestions, so translate the system field key when fetching.
  const companyAttributeKey =
    attributeKey === COMPANY_ID_FIELD_KEY ? "id" : attributeKey;

  const companyQuery = useQuery<Result[], AxiosResponse<ErrorResponse>>({
    queryKey: commonQueryKeys.companyAttributeValues(appId, envId, {
      entityType,
      entityValue,
      attributeKey: companyAttributeKey,
      searchValue,
    }),

    queryFn: () =>
      api
        .get<"/apps/:appId/company-attributes/:attribute/values">(
          `/apps/${appId}/company-attributes/${encodeURIComponent(
            companyAttributeKey!,
          )}/values`,
          {
            params: {
              filter: searchValue ?? "",
              limit: 20,
              envId: envId!,
            },
          },
        )
        .then((res) =>
          res.data.values.map((value) => ({
            label: value,
            value: value,
          })),
        ),

    gcTime: cacheTime,
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    enabled:
      !!appId &&
      !!envId &&
      !!attributeKey &&
      entityType === "company" &&
      attributeKey.trim().length > 0,
  });

  const eventQuery = useQuery<Result[], AxiosResponse<ErrorResponse>>({
    queryKey: commonQueryKeys.eventAttributeValues(
      appId,
      envId,
      entityValue,
      attributeKey,
      searchValue,
    ),

    queryFn: () =>
      api
        .get<"/apps/:appId/events/by-name/:eventName/attributes/:attribute/values">(
          `/apps/${appId}/events/by-name/${encodeURIComponent(
            entityValue!,
          )}/attributes/${encodeURIComponent(attributeKey!)}/values`,
          {
            params: {
              filter: searchValue ?? "",
              limit: 20,
              envId: envId!,
            },
          },
        )
        .then((res) =>
          res.data.values.map((value) => ({
            label: value,
            value: value,
          })),
        ),

    gcTime: cacheTime,
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    enabled:
      !!envId &&
      !!appId &&
      !!attributeKey &&
      entityType === "event" &&
      (entityValue || "").trim().length > 0 &&
      attributeKey.trim().length > 0,
  });

  const flagContextCompanyQuery = useFeatureTargetingContextAttributeValues(
    entityType == "feature-targeting-context-company" ? "company" : undefined,
    attributeKey,
    searchValue,
  );
  const flagContextUserQuery = useFeatureTargetingContextAttributeValues(
    entityType == "feature-targeting-context-user" ? "user" : undefined,
    attributeKey,
    searchValue,
  );
  const flagContextOtherQuery = useFeatureTargetingContextAttributeValues(
    entityType == "feature-targeting-context-other" ? "other" : undefined,
    attributeKey,
    searchValue,
  );

  switch (entityType) {
    case "company":
      return companyQuery;
    case "event":
      return eventQuery;
    case "feature-targeting-context-company":
      return flagContextCompanyQuery;
    case "feature-targeting-context-user":
      return flagContextUserQuery;
    case "feature-targeting-context-other":
      return flagContextOtherQuery;
    case "feature-frequency":
      return {
        data: [
          {
            icon: (
              <FrequencyProgress
                filledColor="brand.400"
                frequency={0}
                mx={0.5}
                showText={false}
                size="2xs"
              />
            ),
            label: "Quarterly",
            value: "0",
          },
          {
            icon: (
              <FrequencyProgress
                filledColor="brand.400"
                frequency={1}
                mx={0.5}
                showText={false}
                size="2xs"
              />
            ),
            label: "Monthly",
            value: "1",
          },
          {
            icon: (
              <FrequencyProgress
                filledColor="brand.400"
                frequency={2}
                mx={0.5}
                showText={false}
                size="2xs"
              />
            ),
            label: "Weekly",
            value: "2",
          },
          {
            icon: (
              <FrequencyProgress
                filledColor="brand.400"
                frequency={3}
                mx={0.5}
                showText={false}
                size="2xs"
              />
            ),
            label: "Daily",
            value: "3",
          },
        ],
        isFetching: false,
      };

    case "feature-satisfaction":
      return {
        data: [
          {
            icon: <FeedbackSatisfactionIcon size="md" value={1} colored />,
            label: "Very dissatisfied",
            value: "1",
          },
          {
            icon: <FeedbackSatisfactionIcon size="md" value={2} colored />,
            label: "Dissatisfied",
            value: "2",
          },
          {
            icon: <FeedbackSatisfactionIcon size="md" value={3} colored />,
            label: "Neutral",
            value: "3",
          },
          {
            icon: <FeedbackSatisfactionIcon size="md" value={4} colored />,
            label: "Satisfied",
            value: "4",
          },
          {
            icon: <FeedbackSatisfactionIcon size="md" value={5} colored />,
            label: "Very satisfied",
            value: "5",
          },
        ],
        isFetching: false,
      };

    case "feature-funnelStep":
      return {
        data: [
          {
            icon: <StarsStateIcon state="never" />,
            label: "Never",
            value: "0",
          },
          {
            icon: <StarsStateIcon state="tried" />,
            label: "Tried",
            value: "2",
          },
          {
            icon: <StarsStateIcon state="churned" />,
            label: "Churned",
            value: "3",
          },
          {
            icon: <StarsStateIcon state="retained" />,
            label: "Retained",
            value: "4",
          },
        ],
        isFetching: false,
      };
    default:
      return { data: [], isFetching: false };
  }
}
